/**
 * Zustand auth store — the SINGLE source of truth for the Worker token + profile
 * + how it was obtained (authMode) + the boot status. Replaces the vanilla
 * split-brain where api.js and api-client.js each held their own `_token`.
 *
 * Only { jwt, authMode, profile } persist (so password/suite sessions survive
 * reload); status/error are runtime-only.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthMode, AuthProfile } from '@humanai/shared';

export const AUTH_STORAGE_KEY = 'evalpilot-auth';

export type AuthStatus = 'loading' | 'ok' | 'no-liff' | 'unpaired' | 'needs-activation' | 'error';

interface AuthState {
  jwt: string | null;
  authMode: AuthMode | null;
  profile: AuthProfile | null;
  status: AuthStatus;
  error: string | null;
  setAuth: (args: { jwt: string; authMode: AuthMode; profile: AuthProfile }) => void;
  setStatus: (status: AuthStatus, error?: string | null) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      jwt: null,
      authMode: null,
      profile: null,
      status: 'loading',
      error: null,
      setAuth: ({ jwt, authMode, profile }) => set({ jwt, authMode, profile }),
      setStatus: (status, error = null) => set({ status, error }),
      clear: () => set({ jwt: null, authMode: null, profile: null }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      partialize: (s) => ({ jwt: s.jwt, authMode: s.authMode, profile: s.profile }),
    },
  ),
);

/** Synchronous JWT check used at boot (before React mounts). */
export function hasPersistedJwt(): boolean {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { state?: { jwt?: string | null } };
    return !!parsed?.state?.jwt;
  } catch {
    return false;
  }
}
